import React, { useState, useEffect } from 'react';
import { BiSearchAlt } from "react-icons/bi";
import { URL } from '../../constant';
import { useLocation } from "react-router-dom";

const CertInfoSec = () => {
  const [searchCert, setSearchCert] = useState('');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false); // Track if search has been performed
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const handleSearch = async () => {
    if (!searchCert.trim()) {
      setSearched(false);
      return; // Just return without alert
    }
    
    setSearching(true);
    setResult(null);
    setSearched(true);
    
    try {
      // Trim and remove any extra whitespace from the certificate number
      const trimmedCertNo = searchCert.trim();
      
      // Use the new search endpoint for certificates
      const response = await fetch(
        `${URL}/data/certificationInfo/search/certificate?certificateNo=${encodeURIComponent(trimmedCertNo)}`
      );
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data.data);
      } else {
        setResult(null);
      }
    } catch (error) {
      console.error("Error searching certificate:", error);
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear result when input changes
  const handleInputChange = (e) => {
    setSearchCert(e.target.value);
    if (searched) {
      setSearched(false);
      setResult(null);
    }
  };

  // Format date to dd-mm-yyyy (optional - if your dates are already formatted from backend)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if not valid date
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  return (
    <div id='certificateinfo' className='my-50 mx-50 flex flex-col gap-6'>
      <div className='flex flex-col items-center '>
        <p className='text-2xl text-darkblue'>CLIENT'S CERTIFICATE INFO</p>
        <p className='text-darkblue'>Please enter your certificate name correctly as available in the certificate.</p>
      </div>

      <div className='w-full flex justify-center gap-3'>
        <input
          className='h-10 w-80 border rounded-lg border-gray-400 focus:border-lightblue indent-2 outline-none'
          type="text"
          placeholder='Certification number'
          value={searchCert}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={searching}
        />
        <button
          className={`h-10 w-12 flex justify-center items-center rounded-lg cursor-pointer ${
            searching ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-buttonfrom to-buttonto hover:opacity-90'
          }`}
          onClick={handleSearch}
          disabled={searching}
        >
          <BiSearchAlt className='text-white text-2xl' />
        </button>
      </div>

      {searching ? (
        <div className='px-7 py-5 bg-lgray shadow-xl rounded-xl'>
          <p className='text-lg font-semibold text-gray-500'>Searching...</p>
        </div>
      ) : result ? (
        <div className='px-7 py-5 bg-lgray shadow-xl rounded-xl'>
          <div className='flex flex-col gap-5'>
            <div className='flex justify-between max-md:flex-col-reverse max-md:gap-3'>
              <div className='flex flex-col'>
                <p className='text-lg font-semibold uppercase'>{result.companyName}</p>
                <p className='font-medium text-gray-500'>{result.district}, {result.state}, {result.country}</p>
              </div>
              <p className='text-lg font-semibold text-gray-500'>{result.certNo}</p>
            </div>
            <div className='w-full border border-gray-200' />
            <div className='flex-1 text-center'>
              <p className='font-medium'>Scope Of Certification</p>
              <p className='font-medium text-gray-500'>{result.scopeOfCertification}</p>
            </div>
            <div className='flex max-md:flex-col max-md:gap-3'>
              <div className='flex flex-1 max-sm:flex-col max-sm:gap-3'>
                <div className='flex-1 text-center'>
                  <p className='font-medium'>Certificate Type</p>
                  <p className='font-medium text-gray-500'>{result.certType}</p>
                </div>
                <div className='flex-1 text-center'>
                  <p className='font-medium'>Expiry Date</p>
                  <p className='font-medium text-gray-500'>{formatDate(result.expiryDate)}</p>
                </div>
                <div className='flex-1 text-center'>
                  <p className='font-medium'>Status</p>
                  <p className={`font-medium ${
                    result.status === 'Valid' ? 'text-green-500' : 
                    result.status === 'Expired' ? 'text-red-500' : 
                    'text-gray-500'
                  }`}>
                    {result.status}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : searched && !searching ? (
        <div className='px-7 py-5 bg-lgray shadow-xl rounded-xl'>
          <p className='text-lg font-semibold text-gray-500'>Enter certificate number correctly</p>
        </div>
      ) : null}

      <div className='w-full flex justify-center'>
        <div className='w-[500px] border border-gray-500' />
      </div>
    </div>
  );
};

export default CertInfoSec;